<script lang="ts">
  import { navigate } from 'svelte-routing';
  import { api } from '../../lib/api';
  import { user, clearAuth } from '../../lib/stores/auth';
  import { showToast } from '../../lib/stores/toast';
  import { withPageTitle } from '../../lib/page-title';
  import { FormSubmit } from '../../lib/form-submit.svelte';
  import Button from '../../components/ui/Button.svelte';
  import Card from '../../components/ui/Card.svelte';
  import Field from '../../components/ui/Field.svelte';
  import Banner from '../../components/ui/Banner.svelte';

  $effect(() => withPageTitle('Delete account'));

  // Account deletion confirmation. Friction-by-design: typing your
  // username gates the destructive button so a misclick or stale tab
  // can't proceed. Server soft-deletes and emails a 7-day undo link
  // (auth-plan §15); we just need to fire the request and clear local
  // state on success.

  let typedName = $state('');

  const ready = $derived(!!$user && typedName.trim().toLowerCase() === $user.username);

  const deleteAccount = new FormSubmit(async () => {
    await api('/api/account', { method: 'DELETE' });
    clearAuth();
    navigate('/?status=deleted', { replace: true });
    // Show toast after nav so it lands on the home shell, not this one.
    showToast(
      'Account deleted. Check your email for the undo link — it stays valid for 7 days.',
      'info',
      9000,
    );
  });

  async function confirm(): Promise<void> {
    if (!ready) return;
    await deleteAccount.submit();
  }
</script>

{#if $user}
  <Card padding="lg" elevated>
    <div class="delete-confirm">
      <header class="delete-confirm__header">
        <h2 class="delete-confirm__title">Delete account</h2>
        <p class="delete-confirm__subtitle">This is reversible — but only for 7 days.</p>
      </header>

      <Banner variant="warn">
        Your account will be soft-deleted immediately. We'll email a restore
        link to <strong>{$user.email}</strong> that stays valid for 7 days.
        After that your data is anonymized and the account can't be recovered.
      </Banner>

      {#if deleteAccount.error && !deleteAccount.error.errors}
        <Banner variant="error">{deleteAccount.error.message}</Banner>
      {/if}

      <Field
        label="Type your username to confirm"
        name="confirm_username"
        autocomplete="off"
        bind:value={typedName}
        hint="Type {$user.username} below."
      />

      <div class="delete-confirm__actions">
        <Button variant="ghost" href="/account">Cancel</Button>
        <Button
          variant="danger"
          loading={deleteAccount.submitting}
          disabled={!ready}
          onclick={confirm}
        >
          Delete my account
        </Button>
      </div>
    </div>
  </Card>
{/if}
